import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import usePlayerUtils from "../../hooks/usePlayerUtils";
import "./RadioPlayerMain.css";
import ProgressBar from "../ProgressBar/ProgressBar";
import VolumeBar from "./VolumeBar";
import { AuthContext } from "../../services/AuthContext";
import usePlayerControls from "../../hooks/usePlayerControls";
import useSyncedTrack from "../../services/useSyncedTrack";

const getModifiedCoverUrl = (url) =>
  url?.includes("default") ? url.replace(".png", "-mid.png") : url;

export default function RadioPlayerHeader() {
  const {
    audioRef,
    currentTrack,
    nowPlaying,
    audioTimeMs,
    playbackState,
    setPlaybackState,
    detachHls,
    setCurrentTimeSec,
    playbackTimeMs,
    currentLiveMs,
    bufferStartMs,
    progressBarPercent,
    durationPercent,
    formatTimestamp,
    setManualTrack,
    setIsSeeking,
    parseDuration,
    setTimeShiftOffsetSec,
    timeShiftOffsetSec,
    elapsedTime
  } = usePlayerUtils();

  const {
    wrapChildClick,
    togglePlay,
  } = usePlayerControls({});

  const liveClockMs = audioTimeMs + timeShiftOffsetSec * 1000;
  const syncedTrack = useSyncedTrack(nowPlaying, liveClockMs, playbackState);
  const liveTrack = syncedTrack || nowPlaying?.history?.[0] || null;
  const liveArtist = liveTrack?.artist;
  const liveSongTitle = liveTrack?.title;

  const artist = currentTrack?.artist;
  const songTitle = currentTrack?.title;
  const songDuration = currentTrack?.duration
  const coverUrl = currentTrack?.coverUrl;

  const { isLoggedIn } = useContext(AuthContext);
  const [volume, setVolume] = useState(1);
  const [hoverInfo, setHoverInfo] = useState(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setCurrentTimeSec(audio.currentTime || 0);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("seeked", onTime);
    audio.addEventListener("loadedmetadata", onTime);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("seeked", onTime);
      audio.removeEventListener("loadedmetadata", onTime);
    };
  }, []);

  const lastVolumeRef = useRef(1);

  const formatTrackSec = (totalSec) => {
    const s = Math.max(0, Math.floor(totalSec));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };

  const handleTrackProgressMove = (e) => {
    const isActive = playbackState === "playing" || playbackState === "paused";
    if (!isActive || !currentTrack?.startedAt || !currentTrack?.duration) {
      setHoverInfo(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);

    const trackDurationSec = parseDuration(currentTrack.duration);
    const startedAtMs = new Date(currentTrack.startedAt).getTime();
    const liveEndMs = audioTimeMs + timeShiftOffsetSec * 1000;
    const maxSecInTrack = (liveEndMs - startedAtMs) / 1000; // не можем да отидем след live

    const sec = Math.min(trackDurationSec * percent, maxSecInTrack);

    setHoverInfo({ x: e.clientX, y: rect.top, sec });
  };

  const handleTrackProgressLeave = () => setHoverInfo(null);

  const handleVolumeChange = (v) => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = v;
    }
    setVolume(v);
    if (v > 0) {
      lastVolumeRef.current = v;
    }
  };

  const toggleMute = () => {
    if (volume > 0) {
      handleVolumeChange(0);
    } else {
      handleVolumeChange(lastVolumeRef.current || 0.5);
    }
  };

  const stopPlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    detachHls();
    setPlaybackState("stopped");
    setCurrentTimeSec(0);
    setTimeShiftOffsetSec(0);
  };

  const handleLiveBtn = (e) => {
    const audio = audioRef.current;
    if (!audio) return;

    handleResumeLive();
    setTimeShiftOffsetSec(0);

    if (playbackState !== "playing") {
      audio.play();
      setPlaybackState("playing");
    }

    audio.currentTime = timeShiftOffsetSec + audio.currentTime;
    setCurrentTimeSec(timeShiftOffsetSec + audio.currentTime);
  }

  const handleResumeLive = () => {
    setIsSeeking(false);
    setManualTrack(null);
  };

  const performSeek = (percent) => {
    // if (!isLoggedIn) {
    //   setUserModalOpen(true);
    //   return;
    // }

    const { maxTimeShiftSeconds, currentLiveTime, history } = nowPlaying || {};

    const audio = audioRef.current;
    if (!audio || !maxTimeShiftSeconds || !currentLiveTime || !history?.length) return;

    const currentLiveMs = new Date(currentLiveTime).getTime();
    const bufferStartMs = currentLiveMs - maxTimeShiftSeconds * 1000;
    const bufferEndMs = currentLiveMs;
    const desiredMs = bufferStartMs + percent * (bufferEndMs - bufferStartMs);

    const seekSec = (desiredMs - bufferStartMs) / 1000;
    const realCurrentTime = timeShiftOffsetSec + audio.currentTime;
    const currentSeekSec = realCurrentTime - maxTimeShiftSeconds + seekSec;

    const newOffset = realCurrentTime - currentSeekSec;
    setTimeShiftOffsetSec(newOffset);

    audio.currentTime = currentSeekSec;
    setCurrentTimeSec(currentSeekSec);

    const seekTimestamp = bufferStartMs + currentSeekSec * 1000;

    const currentSong = history.find(song => {
      const start = new Date(song.startedAt).getTime();
      const end = start + parseDuration(song.duration) * 1000;
      return seekTimestamp >= start && seekTimestamp < end;
    });

    if (currentSong) {
      setManualTrack(currentSong || null);
      setIsSeeking(true);
    }
  };

  const sortedHistory = useMemo(
    () =>
      [...(nowPlaying?.history || [])].sort(
        (a, b) => new Date(a.startedAt) - new Date(b.startedAt)
      ),
    [nowPlaying?.history]
  );

  const planSeek = (dir) => {
    const { maxTimeShiftSeconds } = nowPlaying || {};
    const audio = audioRef.current;

    if (!audio || !maxTimeShiftSeconds || !currentTrack?.startedAt) return null;
    if (playbackState === "stopped" || audioTimeMs <= 0) return null;

    const idx = sortedHistory.findIndex(s => s.startedAt === currentTrack.startedAt);
    if (idx === -1) return null;

    const target = sortedHistory[idx + dir];
    if (!target) return null;

    const targetMs = new Date(target.startedAt).getTime();

    if (targetMs > audioTimeMs + timeShiftOffsetSec * 1000) return null;

    const bufStartMs = audioTimeMs - (maxTimeShiftSeconds - timeShiftOffsetSec) * 1000;
    const realCurrentTime = timeShiftOffsetSec + audio.currentTime;
    const realCurrentMs = bufStartMs + realCurrentTime * 1000;

    if (targetMs > realCurrentMs) {
      return { track: target, goLive: true, realCurrentTime };
    }

    const percent = (targetMs - bufStartMs) / (maxTimeShiftSeconds * 1000);
    const desiredSec = realCurrentTime - maxTimeShiftSeconds + percent * maxTimeShiftSeconds;

    if (desiredSec <= 0) return null;

    return {
      track: target,
      goLive: false,
      desiredSec,
      newOffset: realCurrentTime - desiredSec,
    };
  };

  const seekAdjacent = (dir) => {
    const audio = audioRef.current;
    const plan = planSeek(dir);
    if (!audio || !plan) return;

    if (plan.goLive) {
      audio.currentTime = plan.realCurrentTime;
      setTimeShiftOffsetSec(0);
      handleResumeLive();
      return;
    }

    setTimeShiftOffsetSec(plan.newOffset);
    audio.currentTime = plan.desiredSec;
    setCurrentTimeSec(plan.desiredSec);

    setManualTrack(plan.track);
    setIsSeeking(true);
  };

  const seekInCurrentTrack = (clickPercent) => {
    const { maxTimeShiftSeconds, currentLiveTime, history } = nowPlaying || {};
    const audio = audioRef.current;

    if (!audio || !maxTimeShiftSeconds || !currentLiveTime || !history?.length) return;
    if (playbackState === "stopped" || audioTimeMs <= 0) return;
    if (!currentTrack?.startedAt || !currentTrack?.duration) return;

    const startedAtMs = new Date(currentTrack.startedAt).getTime();
    const trackDurationMs = parseDuration(currentTrack.duration) * 1000;

    const bufferStartMs = audioTimeMs - (maxTimeShiftSeconds - timeShiftOffsetSec) * 1000;
    const liveEndMs = audioTimeMs + timeShiftOffsetSec * 1000; // края на буфера (live)

    // къде искаме да отидем, не по-напред от live
    const targetMs = Math.min(startedAtMs + trackDurationMs * clickPercent, liveEndMs);

    const realCurrentTime = timeShiftOffsetSec + audio.currentTime;

    const percent = (targetMs - bufferStartMs) / (maxTimeShiftSeconds * 1000);
    const desiredSec = realCurrentTime - maxTimeShiftSeconds + percent * maxTimeShiftSeconds;

    if (desiredSec <= 0) return; // излиза извън буфера

    const newOffset = realCurrentTime - desiredSec;

    setTimeShiftOffsetSec(newOffset);
    audio.currentTime = desiredSec;
    setCurrentTimeSec(desiredSec);

    setManualTrack(currentTrack);
    setIsSeeking(true);
  };

  const handleTrackProgressClick = (e) => {
    const element = e.currentTarget;
    const clientX = e.clientX;

    wrapChildClick(() => {
      const rect = element.getBoundingClientRect();
      const clickPercent = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
      seekInCurrentTrack(clickPercent);
    })();
  };

  const canPrev = !!planSeek(-1);
  const canNext = !!planSeek(1);

  const liveRadioProgress = () => {
    const liveCoverUrl = getModifiedCoverUrl(liveTrack?.coverUrl);

    return (
      <div className="d-live-progress-content">
        <div className="d-live-progress-content-inner">
          <div className="d-live-progress-inner">
            {playerProgressInfo()}
          </div>
          <div className="d-track-placeholder">
            <div className="d-track-placeholder-inner">
              <div className="d-track-placeholder-live">Live</div>
              <div className="d-track-live-info">
                <div className="d-colapsed-cover">
                  {liveCoverUrl && (<img className="d-live-cover" src={liveCoverUrl} alt="Live cover" />)}
                </div>
                <div className="d-song-info">
                  <div className="d-colapsed-song-info">
                    <div className="d-song-title" title={liveSongTitle}>{liveSongTitle}</div>
                    <div className="d-artist" title={liveArtist}>{liveArtist}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const playerProgressInfo = () => {
    const liveBtnClassName = timeShiftOffsetSec <= 0 || playbackState === "stopped" ? "live-btn-disabled live-button" : "live-button";

    return (
      <div className="d-status-progerss">
        <button className={liveBtnClassName} onClick={wrapChildClick(() => handleLiveBtn())} disabled={timeShiftOffsetSec <= 0 || playbackState === "stopped"} >
          <div className="live-text">GO LIVE</div>
        </button>
        <div className="colapsed-times">
          <div className="d-time">{bufferStartMs ? formatTimestamp(bufferStartMs) : "--:--:--"}</div>
          <div className="d-time d-playbeck-time">
            <div>{playbackTimeMs ? new Date(playbackTimeMs).toLocaleTimeString("en-GB", { hour12: false }) : "--:--:--"}</div>
          </div>
          <div className="d-time">{currentLiveMs ? formatTimestamp(currentLiveMs) : "--:--:--"}</div>
        </div>
        <div className="colapsed-progress">
          <ProgressBar progressPercent={progressBarPercent / 100} onSeek={performSeek} />
        </div>
      </div>
    )
  }

  const playerControll = () => {
    const stopBtnClassName = playbackState === "stopped" ? "btn-disabled stop-btn" : "stop-btn";

    const modifiedCoverUrl = getModifiedCoverUrl(coverUrl);

    return (
      <div className="d-cover-info-btn">
        <div className="d-colapsed-info">
          {(playbackState === "playing" || playbackState === "paused") &&
            <>
              <div className="d-colapsed-cover">
                <img className="d-colapsed-img-cover d-colapsed-fade" src={modifiedCoverUrl} alt="Cover" />
              </div>
              <div className="d-song-info">
                <div className="d-colapsed-song-info">
                  <div className="d-song-title" title={songTitle}>{songTitle}</div>
                  <div className="d-artist" title={artist}>{artist}</div>
                </div>
              </div>
            </>
          }
        </div>
        <div className="d-track-info-main">
          <div className="d-track-btn-times">
            <div className="d-track-times">
              {(playbackState === "playing" || playbackState === "paused") &&
                <div className="d-player-indications">
                  <div className="d-player-duration">
                    <div>{elapsedTime()}</div>
                  </div>
                </div>
              }
            </div>
            <div className="d-track-btns">
              <div className="colapsed-btn d-end-btn-position">
                <div className="prev-for-btn">
                  <button
                    style={{ padding: "0px 8px 0px 4px" }}
                    className={canPrev ? "d-btn-prev-for" : "d-btn-prev-for-disabled"}
                    onClick={wrapChildClick(() => seekAdjacent(-1))}
                    disabled={!canPrev}
                  >
                    <i className="fas fa-backward"></i>
                  </button>
                </div>
              </div>

              <div className="colapsed-btn d-end-btn-position">
                <div className="colapsed-player-btn">
                  <button className="play-btn" onClick={wrapChildClick(() => togglePlay())}>
                    {playbackState === "playing"
                      ? <i className="fas fa-pause"></i>
                      : <i className="fas fa-play"></i>
                    }
                  </button>
                </div>
              </div>
              <div className="colapsed-btn d-start-btn-position">
                <div className="stop-btn-ring" style={{ "--percent": "100%" }}>
                  <button className={stopBtnClassName} onClick={wrapChildClick(() => stopPlayback())} disabled={playbackState === "stopped"}>
                    <i className="fas fa-stop"></i>
                  </button>
                </div>
              </div>

              <div className="colapsed-btn d-start-btn-position">
                <div className="prev-for-btn">
                  <button
                    style={{ padding: "0px 4px 0px 8px" }}
                    className={canNext ? "d-btn-prev-for" : "d-btn-prev-for-disabled"}
                    onClick={wrapChildClick(() => seekAdjacent(1))}
                    disabled={!canNext}
                  >
                    <i className="fas fa-forward"></i>
                  </button>
                </div>
              </div>
            </div>
            <div className="d-track-times">
              {(playbackState === "playing" || playbackState === "paused") &&
                <div className="d-player-indications">
                  <div className="d-player-duration">
                    <div>{songDuration}</div>
                  </div>
                </div>
              }
            </div>
          </div>
          <div className="d-track-progress-times">
            <div className="progress-track"
              onMouseMove={handleTrackProgressMove}
              onMouseLeave={handleTrackProgressLeave}
              onClick={handleTrackProgressClick}
              style={{ cursor: (playbackState === "playing" || playbackState === "paused") ? "pointer" : "default" }}
            >
              <div className="progress-playsholder"></div>
              {(playbackState === "playing" || playbackState === "paused") &&
                <div
                  className="progress-filled"
                  style={{
                    width: `${durationPercent() * 100}%`,
                    backgroundColor: playbackState === "playing"
                      ? "rgb(179, 177, 177)"
                      : "rgb(127, 127, 127)",
                  }}
                ></div>}
              {hoverInfo && (
                <div
                  className="progress-hover-tooltip"
                  style={{ left: hoverInfo.x, top: hoverInfo.y }}
                >
                  {formatTrackSec(hoverInfo.sec)}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="d-track-placeholder">
          {volumeBar()}
        </div>
      </div>
    )
  }

  const volumeBar = () => {
    return (
      <div className="d-volume-section">
        <div className="d-volume-inner">
          <i
            className={`fas ${volume < 0.01 ? 'fa-volume-mute' : volume < 0.5 ? 'fa-volume-down' : 'fa-volume-up'}`}
            onClick={toggleMute}
          ></i>
          <div className="d-volume-bar-content">
            <VolumeBar value={volume} onChange={handleVolumeChange} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="d-live-progress">
        {liveRadioProgress()}
      </div>
      <div className="radio-player-main">
        <div className="player-main">
          {playerControll()}
        </div>
      </div>
    </>
  );
}
